import React, { useEffect } from 'react';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** id of the element that names the dialog (its heading) */
  labelledBy?: string;
  /** id of the element that describes the dialog */
  describedBy?: string;
  /** Close when Escape is pressed (default true) */
  closeOnEscape?: boolean;
  /** Hide the corner close button (default false) */
  hideCloseButton?: boolean;
  /** Panel width: md (default, max-w-lg) or xl (max-w-3xl, long forms) */
  size?: 'md' | 'xl';
}

const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  children,
  labelledBy,
  describedBy,
  closeOnEscape = true,
  hideCloseButton = false,
  size = 'md',
}) => {
  useEffect(() => {
    if (!open || !closeOnEscape) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Handled: other document listeners (review shortcuts) skip a prevented event
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div
        className={`bg-white rounded-lg shadow-lg ${size === 'xl' ? 'max-w-3xl' : 'max-w-lg'} w-full p-6 relative overflow-y-auto max-h-[90vh]`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
      >
        {!hideCloseButton && (
          <button
            type="button"
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl font-bold"
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

export default Dialog;
